import { ReactElement, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useElementHistory } from '@hooks'
import { SelectedElementProblemsList } from '@structuralPlanningComponents'
import { AxiosError } from 'axios'
import { find } from 'lodash-es'
import { Box, Stack, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FocusButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { useEditElementStore, useModelStore } from '@editorStores'
import { useBlockScene, useResultsInvalidation } from '@editorHooks'
import { useModelInvalidation, useStructuralPlanningFormEsc } from '@structuralPlanningHooks'
import { getModel } from '@queries'
import { createOpening, deleteOpening, updateOpening as updateOpeningRequest } from '@mutations'
import { errors } from 'src/constants'
import { buildErrorMessage } from 'src/constants/errors'
import StructuralPlanningDeselectButton from '../StructuralPlanningDeselectButton'
import WallOpeningCopyForm from '../WallOpeningCopyForm'
import GeometryForm from './components/GeometryForm'

interface Props {
  selectedWallId: string | null
  selectedOpeningId: string | null
  deselectWall: () => void
  /** Clears the current selection (Structural Planning passes its query-param `setElements`). */
  setElements: (elements: string[]) => void
  /**
   * Switches the editing mode (e.g. `draw-openings` for the "in Draufsicht bearbeiten" action).
   * When omitted, the "in Draufsicht bearbeiten" action is hidden (e.g. in the IFC importer, which
   * has no top-view drawing mode).
   */
  toggleMode?: (newMode?: StructuralPlanningModes) => void
  mode?: '2D' | '3D'
  /** Shows "abwählen" (calls `deselectWall`) for a saved opening; off when nested in a wall form. */
  showDeselectButton?: boolean
}

const OpeningForm = ({
  selectedOpeningId,
  selectedWallId,
  deselectWall,
  setElements,
  toggleMode,
  mode,
  showDeselectButton = false,
}: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()

  const { projectId } = useParams()

  const invalidateResults = useResultsInvalidation()

  const { walls } = useModelStore(state => state.model)
  const removeOpening = useModelStore(state => state.removeOpening)
  const updateOpening = useModelStore(state => state.updateOpening)

  const invalidateModel = useModelInvalidation()

  const setActiveOpening = useEditElementStore(state => state.setActiveOpening)
  const setActiveElement = useEditElementStore(state => state.setActiveElement)

  const queryClient = useQueryClient()

  // MEMOS

  const wall = useMemo(() => find(walls, { guid: selectedWallId }), [walls, selectedWallId]) as
    | ShapeObject
    | undefined

  const selectedOpening = useMemo(
    () => (selectedOpeningId ? find(wall?.openings, { guid: selectedOpeningId }) : undefined),
    [wall, selectedOpeningId],
  )

  // TODO: check if esc handling is still working
  const { setStale: setOpeningStale } = useElementHistory({
    element: selectedOpening || null,
    getCurrentElement: () => {
      const walls = useModelStore.getState().model.walls
      const currentWall = find(walls, { guid: wall?.guid })
      const opening = find(currentWall?.openings, { guid: selectedOpeningId }) as
        | Opening
        | undefined
      return opening || null
    },
    removeElement: opening => opening && wall && removeOpening(wall.guid, opening.guid),
    resetElement: opening => opening && wall && updateOpening(wall.guid, opening),
    dependencies: [wall?.guid, selectedOpeningId],
  })

  const { mutateAsync, isLoading } = useMutation(
    (opening: Opening) =>
      createOpening.request(projectId, {
        element_guid: selectedWallId,
        shape: {
          points: opening.shape.points,
        },
      }),
    {
      onSuccess: async (data: ShapeObject) => {
        const opening = find(data.openings, { guid: selectedOpeningId as string })!!
        setOpeningStale(opening)
        // Deselect just the saved opening (not the wall/mode) so "in Draufsicht bearbeiten" stays
        // active and the user can keep drawing further openings on the same wall.
        setActiveOpening(null)
        await invalidateModel(projectId as string)
        await queryClient.invalidateQueries(getModel.getKey(projectId))

        invalidateResults(projectId as string)
        enqueueSnackbar('Öffnung erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        if (errors[error.response?.data.code]) {
          enqueueSnackbar(errors[error.response?.data.code], { variant: 'error' })
          return
        }
        enqueueSnackbar('Fehler beim Speichern der Öffnung', { variant: 'error' })
      },
    },
  )

  const { mutateAsync: handleDelete, isLoading: isDeleting } = useMutation(
    ({ wallGuid, openingGuid }: { wallGuid: string; openingGuid: string }) =>
      deleteOpening.request(projectId, wallGuid, openingGuid),
    {
      onSuccess: async (_data, { wallGuid, openingGuid }) => {
        // Stale history must be set before the panel unmounts: the unmount
        // cleanup otherwise resets the opening into the store.
        if (selectedOpening) setOpeningStale(selectedOpening)
        removeOpening(wallGuid, openingGuid)
        if (projectId) {
          await invalidateModel(projectId)
          await queryClient.invalidateQueries(getModel.getKey(projectId))
        }
        invalidateResults(projectId as string)
        setElements([])
        setActiveOpening(null)
        enqueueSnackbar('Öffnung erfolgreich gelöscht', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen der Öffnung'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: updateAsync, isLoading: isUpdating } = useMutation(
    (opening: Opening) =>
      updateOpeningRequest.request(projectId, {
        element_guid: selectedWallId,
        ...opening,
      }),
    {
      onSuccess: async () => {
        deselectWall()
        await invalidateModel(projectId as string)
        await queryClient.invalidateQueries(getModel.getKey(projectId))

        invalidateResults(projectId as string)
        enqueueSnackbar('Öffnung erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        if (errors[error.response?.data.code]) {
          enqueueSnackbar(errors[error.response?.data.code], { variant: 'error' })
          return
        }
        enqueueSnackbar('Fehler beim Speichern der Öffnung', { variant: 'error' })
      },
    },
  )

  useBlockScene(isLoading || isDeleting)

  useStructuralPlanningFormEsc(() => {
    deselectWall()
  }, !!selectedWallId)

  if (!selectedWallId || !wall) {
    return (
      <>
        <Box p={1} border={1} borderColor="grey.200" borderRadius={1} mb={2}>
          <Typography mb={1}>
            Sie haben nun die Möglichkeit eine Wand anzuwählen um dort eine Öffnung einzuzeichnen
          </Typography>
        </Box>
      </>
    )
  }

  if (!selectedOpeningId) {
    return (
      <>
        <Box p={1} border={1} borderColor="grey.200" borderRadius={1} mb={2}>
          <Typography mb={1}>
            Wählen sie eine bestehende Öffnung oder zeichnen Sie eine neue ein
          </Typography>
        </Box>
      </>
    )
  }

  if (selectedOpening?.is_local) {
    return (
      <>
        <SelectedElementProblemsList
          selectedElement={selectedWallId}
          selectedOpeningGuid={selectedOpeningId}
        />
        {selectedOpening && (
          <>
            <Stack direction="column" spacing={1} alignItems="center">
              {toggleMode && (
                <FocusButton
                  onClick={() => {
                    if (wall) {
                      setActiveElement(wall.guid)
                      toggleMode('draw-openings')
                    }
                  }}
                  size="small"
                  variant="outlined"
                >
                  in Draufsicht bearbeiten
                </FocusButton>
              )}
              <WallOpeningCopyForm sourceWall={wall} opening={selectedOpening} />
            </Stack>
          </>
        )}
        <GeometryForm
          openingGuid={selectedOpeningId}
          wall={wall}
          mode={mode}
          handleSubmit={mutateAsync}
          isSubmitting={isLoading}
          isDeleting={isDeleting}
          handleDelete={() => removeOpening(selectedWallId, selectedOpeningId)}
        />
      </>
    )
  }

  return (
    <>
      {showDeselectButton && (
        <Box p={1}>
          <StructuralPlanningDeselectButton onDeselect={deselectWall} />
        </Box>
      )}
      {selectedOpening && (
        <>
          <Stack direction="column" spacing={1} alignItems="center">
            {toggleMode && (
              <FocusButton
                onClick={() => {
                  if (wall) {
                    setActiveElement(wall.guid)
                    toggleMode('draw-openings')
                  }
                }}
                size="small"
                variant="outlined"
              >
                in Draufsicht bearbeiten
              </FocusButton>
            )}

            <WallOpeningCopyForm sourceWall={wall} opening={selectedOpening} />
          </Stack>
        </>
      )}
      <GeometryForm
        openingGuid={selectedOpeningId}
        wall={wall}
        mode={mode}
        handleSubmit={updateAsync}
        isSubmitting={isUpdating}
        isDeleting={isDeleting}
        handleDelete={() =>
          handleDelete({ wallGuid: selectedWallId, openingGuid: selectedOpeningId })
        }
      />
      <Box p={1}>
        <SelectedElementProblemsList
          selectedElement={selectedWallId}
          selectedOpeningGuid={selectedOpeningId}
        />
      </Box>
    </>
  )
}

export default OpeningForm
